import { cn } from "../../lib/cn.js";
import { istDate, istMinutesOfDay } from "../../lib/format.js";
import type { HealthResponse } from "../../lib/queries.js";
import { Section } from "../ui/Section.js";

type Session = HealthResponse["data"]["sessions"][number];

/** `formatNumber` in lib/format.ts is toFixed and adds no separators; counts want them. */
function count(value: number): string {
  return value.toLocaleString("en-IN");
}

/** Below this share of the median symbol count, a session reads as short. */
const SHORT_SHARE = 0.7;

/** The hourly universe is 500 names; a session under this many is a broken ingest. */
const HOURLY_FLOOR = 400;

/**
 * When the bars job fills today's daily row: 18:30 IST.
 *
 * Mirrors `istCron("18:30")` in `apps/worker/src/schedule.ts`, the same way
 * `KiteStatusCard` mirrors the login poll window. Duplicated rather than
 * served from the API, which would mean a Health field whose only reader is
 * one note on one row.
 */
const BARS_DUE_IST = 18 * 60 + 30;

/**
 * Has today's bars job had its turn yet?
 *
 * Hourly bars create today's row from 09:15, and the daily count stays 0
 * until 18:30, so between those two the newest row reads as a hole in a
 * table whose whole point is finding holes. Nine warning-coloured hours a
 * day is how a reader learns to skip the column.
 *
 * Only today is excused, and only until 18:30. A bars job that then fails
 * shows as a failed row in the Pipelines table above, and by the next
 * morning this row flags on its own.
 */
export function barsDue(sessionDate: string, now: Date): boolean {
  if (sessionDate !== istDate(now)) return true;
  return istMinutesOfDay(now) >= BARS_DUE_IST;
}

export interface SessionNote {
  text: string;
  /** False when the note is "not due yet", so the row keeps its normal colour. */
  warn: boolean;
}

export function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length === 0 ? 0 : (sorted[mid] as number);
}

/**
 * One phrase per session, or nothing.
 *
 * A session only hourly bars know about is the calendar bug of 2026-09-03;
 * a short one is a truncated bhavcopy; a thin hourly one is a Kite outage
 * that pipeline_runs may have recorded as ok for the buckets it did write.
 */
export function flagSession(
  session: Session,
  medianSymbols: number,
  dailyDue = true,
): SessionNote | null {
  if (session.symbols === 0) {
    return dailyDue
      ? { text: "no daily bars", warn: true }
      : { text: "daily bars due 18:30", warn: false };
  }
  if (medianSymbols > 0 && session.symbols < medianSymbols * SHORT_SHARE) {
    return { text: `short: ${count(session.symbols)} of ~${count(medianSymbols)}`, warn: true };
  }
  if (session.hourlySymbols > 0 && session.hourlySymbols < HOURLY_FLOOR) {
    return { text: `hourly: ${session.hourlySymbols} symbols`, warn: true };
  }
  return null;
}

/**
 * Per-session counts, which whole-table counts cannot show.
 *
 * A bar count going from 8.5M to 8.6M says nothing about whether last
 * Tuesday landed, and a session with 200 of 3,100 symbols reads as a
 * successful run in pipeline_runs.
 */
export function DataCompletenessCard({
  data,
  now = new Date(),
}: {
  data: HealthResponse["data"];
  now?: Date;
}) {
  const medianSymbols = median(data.sessions.map((s) => s.symbols).filter((n) => n > 0));
  return (
    <Section title="Data completeness">
      <dl className="flex justify-between gap-4 text-sm text-text-muted">
        <dt>Pending invalidations</dt>
        <dd
          className={cn(
            "tabular-nums",
            data.invalidations.pending > 0 ? "text-warning" : "text-text",
          )}
        >
          {data.invalidations.pending}
        </dd>
      </dl>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border text-text-muted">
            <th className="py-1 text-left font-medium">Session</th>
            <th className="py-1 text-right font-medium">Daily</th>
            <th className="py-1 text-right font-medium">Hourly</th>
            <th className="py-1 pl-4 text-left font-medium">Note</th>
          </tr>
        </thead>
        <tbody>
          {data.sessions.map((session) => {
            const flag = flagSession(session, medianSymbols, barsDue(session.sessionDate, now));
            return (
              <tr
                key={session.sessionDate}
                className={cn(
                  "border-b border-border/50",
                  flag?.warn ? "text-warning" : "text-text",
                )}
              >
                <td className="py-1 tabular-nums">{session.sessionDate}</td>
                <td className="py-1 text-right tabular-nums">{count(session.symbols)}</td>
                <td className="py-1 text-right tabular-nums">
                  {session.hourlySymbols}/{count(session.hourlyBuckets)}
                </td>
                <td className="py-1 pl-4">{flag?.text ?? ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Section>
  );
}
